import { Tearsheet } from '@carbon/ibm-products';
import { Loading } from '@carbon/react';
import { useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import sharedMessages from 'Components/utils/sharedMessages';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import { useCreateAnnouncementMutation } from 'api/announcementEndpoints';
import AnnouncementForm, {
    AnnouncementFormHandle,
    AnnouncementFormValues,
} from '../AnnouncementForm/AnnouncementForm';

import styles from './AddAnnouncementView.module.scss';

type Props = {
    open?: boolean;
    onClose?: () => void;
}

const AddAnnouncementView = ({
    open = true,
    onClose,
}: Props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const currentEntityId = useGetCurrentEntityId();
    const [createAnnouncement, { isLoading }] = useCreateAnnouncementMutation();
    const formRef = useRef<AnnouncementFormHandle>(null);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');

    const handleClose = () => {
        if (onClose) {
            onClose();
            return;
        }

        navigate('/announcements');
    };

    const handleSubmit = async (values: AnnouncementFormValues) => {
        if (!currentEntityId) return;
        setErrorMessage('');

        try {
            await createAnnouncement({
                entityId: currentEntityId,
                data: {
                    title: values.title,
                    content: values.content,
                    publishedAt: new Date().toISOString(),
                },
            }).unwrap();
            handleClose();
        } catch {
            setErrorMessage(intl.formatMessage(messages.submitError));
        }
    };

    return (
        <>
            {isLoading && <Loading />}
            <Tearsheet
                open={open}
                title={intl.formatMessage(messages.add)}
                description={intl.formatMessage(messages.description)}
                closeIconDescription={intl.formatMessage(sharedMessages.close)}
                hasCloseIcon
                onClose={handleClose}
                selectorPrimaryFocus="#announcement-title"
                actions={[
                    {
                        label: intl.formatMessage(sharedMessages.cancel),
                        kind: 'secondary',
                        onClick: handleClose,
                    },
                    {
                        label: intl.formatMessage(sharedMessages.submit),
                        kind: 'primary',
                        disabled: isLoading || isSubmitDisabled,
                        loading: isLoading,
                        onClick: () => formRef.current?.submit(),
                    },
                ]}
            >
                {open && (
                    <div className={styles.form}>
                        {errorMessage && (
                            <div className={styles.error}>{errorMessage}</div>
                        )}
                        <AnnouncementForm
                            ref={formRef}
                            onSubmit={handleSubmit}
                            isLoading={isLoading}
                            hideActions
                            onDisabledChange={setIsSubmitDisabled}
                        />
                    </div>
                )}
            </Tearsheet>
        </>
    );
};

const messages = defineMessages({
    add: {
        id: 'announcements.add.title',
        defaultMessage: 'Dodaj ogłoszenie',
    },
    description: {
        id: 'announcements.add.description',
        defaultMessage: 'Wypełnij dane nowego ogłoszenia parafialnego.',
    },
    submitError: {
        id: 'announcements.add.submitError',
        defaultMessage: 'Nie udało się dodać ogłoszenia. Sprawdź dane i spróbuj ponownie.',
    },
});

export default AddAnnouncementView;
